/*
 * HomeFixer360 - Admin CRM Dashboard
 * Copyright (c) 2025 Guy T. Dunn Sr. / Idea Guy Brands Incorporated
 * All Rights Reserved.
 */

import React, { useState, useEffect } from 'react';
import { 
  Users, Mail, Handshake, Search, Filter, Download, 
  CheckCircle, XCircle, Clock, Eye, Phone, MapPin,
  TrendingUp, DollarSign, Calendar, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';

const AdminCRM = () => {
  const [activeTab, setActiveTab] = useState('leads');
  const [leads, setLeads] = useState([]);
  const [partners, setPartners] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState({
    totalLeads: 0,
    totalPartners: 0,
    totalContacts: 0,
    pendingLeads: 0,
    respondedContacts: 0
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      if (activeTab === 'leads') {
        await loadLeads();
      } else if (activeTab === 'partners') {
        await loadPartners();
      } else if (activeTab === 'contacts') {
        await loadContacts();
      }
      await loadStats();
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadLeads = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/leads/`);
      setLeads(response.data);
    } catch (error) {
      console.error('Error loading leads:', error);
    }
  };

  const loadPartners = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/partners/applications`);
      setPartners(response.data);
    } catch (error) {
      console.error('Error loading partners:', error);
    }
  };

  const loadContacts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/contact/submissions`);
      setContacts(response.data || []);
    } catch (error) {
      console.error('Error loading contacts:', error);
      setContacts([]);
    }
  };

  const loadStats = async () => {
    try {
      const [leadsRes, partnersRes, contactsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/leads/`),
        axios.get(`${BACKEND_URL}/api/partners/applications`),
        axios.get(`${BACKEND_URL}/api/contact/submissions`)
      ]);

      setStats({
        totalLeads: leadsRes.data.length,
        totalPartners: partnersRes.data.length,
        totalContacts: contactsRes.data?.length || 0,
        pendingLeads: leadsRes.data.filter(l => l.status === 'new' || l.status === 'pending').length,
        respondedContacts: contactsRes.data?.filter(c => c.responded).length || 0
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleStatusUpdate = async (id, newStatus, type) => {
    try {
      if (type === 'lead') {
        await axios.put(`${BACKEND_URL}/api/leads/${id}`, { status: newStatus });
        loadLeads();
      } else if (type === 'contact') {
        await axios.put(`${BACKEND_URL}/api/contact/submissions/${id}`, { responded: true });
        loadContacts();
      }
      loadStats();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const exportToCSV = () => {
    let data = [];
    let filename = '';

    if (activeTab === 'leads') {
      data = filteredLeads.map(lead => ({
        Name: `${lead.first_name} ${lead.last_name}`,
        Email: lead.email,
        Phone: lead.phone,
        Address: lead.address,
        Status: lead.status,
        Created: new Date(lead.created_at).toLocaleDateString()
      }));
      filename = 'leads.csv';
    } else if (activeTab === 'partners') {
      data = filteredPartners.map(partner => ({
        Name: `${partner.first_name} ${partner.last_name}`,
        Email: partner.email,
        Company: partner.company_name,
        Type: partner.partner_type,
        Status: partner.status,
        Created: new Date(partner.created_at).toLocaleDateString()
      }));
      filename = 'partners.csv';
    } else if (activeTab === 'contacts') {
      data = filteredContacts.map(contact => ({
        Name: `${contact.first_name} ${contact.last_name}`,
        Email: contact.email,
        Phone: contact.phone,
        Company: contact.company_name,
        City: contact.city,
        Reason: contact.reason,
        Responded: contact.responded ? 'Yes' : 'No',
        Created: new Date(contact.created_at).toLocaleDateString()
      }));
      filename = 'contacts.csv';
    }

    // Convert to CSV
    const headers = Object.keys(data[0] || {}).join(',');
    const rows = data.map(row => Object.values(row).join(','));
    const csv = [headers, ...rows].join('\n');

    // Download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800',
      contacted: 'bg-purple-100 text-purple-800',
      qualified: 'bg-green-100 text-green-800',
      converted: 'bg-green-600 text-white',
      lost: 'bg-red-100 text-red-800',
      processed: 'bg-green-100 text-green-800',
      approved: 'bg-green-600 text-white',
      rejected: 'bg-red-100 text-red-800',
      under_review: 'bg-yellow-100 text-yellow-800'
    };

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status?.replace('_', ' ').toUpperCase() || 'NEW'}
      </span>
    );
  };

  // Filter data
  const filteredLeads = leads.filter(lead => {
    const matchesSearch = 
      `${lead.first_name} ${lead.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.phone?.includes(searchTerm);
    const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredPartners = partners.filter(partner => {
    const matchesSearch = 
      `${partner.first_name} ${partner.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      partner.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      partner.company_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || partner.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = 
      `${contact.first_name} ${contact.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.company_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'responded' && contact.responded) ||
      (statusFilter === 'pending' && !contact.responded);
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">CRM Dashboard</h1>
          <p className="text-gray-600 mt-1">Manage leads, partners, and customer inquiries</p>
        </div>
        <Button
          onClick={loadData}
          variant="outline"
          className="flex items-center space-x-2"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Refresh</span>
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Leads</p>
              <p className="text-2xl font-bold text-blue-600">{stats.totalLeads}</p>
            </div>
            <Users className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending Leads</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.pendingLeads}</p>
            </div>
            <Clock className="w-8 h-8 text-yellow-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Partner Apps</p>
              <p className="text-2xl font-bold text-purple-600">{stats.totalPartners}</p>
            </div>
            <Handshake className="w-8 h-8 text-purple-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Contact Forms</p>
              <p className="text-2xl font-bold text-green-600">{stats.totalContacts}</p>
            </div>
            <Mail className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Responded</p>
              <p className="text-2xl font-bold text-green-600">{stats.respondedContacts}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('leads')}
            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
              activeTab === 'leads'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            Leads ({stats.totalLeads})
          </button>
          <button
            onClick={() => setActiveTab('partners')}
            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
              activeTab === 'partners'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Handshake className="w-4 h-4 inline mr-2" />
            Partner Applications ({stats.totalPartners})
          </button>
          <button
            onClick={() => setActiveTab('contacts')}
            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
              activeTab === 'contacts'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Mail className="w-4 h-4 inline mr-2" />
            Contact Forms ({stats.totalContacts})
          </button>
        </div>

        {/* Filters */}
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex space-x-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Search by name, email, or company..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="w-48">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Status</option>
                {activeTab === 'leads' && (
                  <>
                    <option value="new">New</option>
                    <option value="pending">Pending</option>
                    <option value="contacted">Contacted</option>
                    <option value="qualified">Qualified</option>
                    <option value="converted">Converted</option>
                    <option value="lost">Lost</option>
                  </>
                )}
                {activeTab === 'partners' && (
                  <>
                    <option value="pending">Pending</option>
                    <option value="under_review">Under Review</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </>
                )}
                {activeTab === 'contacts' && (
                  <>
                    <option value="pending">Pending</option>
                    <option value="responded">Responded</option>
                  </>
                )}
              </select>
            </div>
            <Button
              onClick={exportToCSV}
              variant="outline"
              className="flex items-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>Export CSV</span>
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {isLoading ? (
            <div className="text-center py-12">
              <p className="text-gray-600">Loading...</p>
            </div>
          ) : (
            <>
              {/* Leads Table */}
              {activeTab === 'leads' && (
                <div className="overflow-x-auto">
                  {filteredLeads.length === 0 ? (
                    <div className="text-center py-12">
                      <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">No leads found</p>
                    </div>
                  ) : (
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Address</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {filteredLeads.map((lead) => (
                          <tr key={lead.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div className="font-medium text-gray-900">{lead.first_name} {lead.last_name}</div>
                              <div className="text-sm text-gray-500">{lead.email}</div>
                              <div className="text-sm text-gray-500">{lead.phone}</div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">{lead.address}</td>
                            <td className="px-4 py-3">{getStatusBadge(lead.status)}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{formatDate(lead.created_at)}</td>
                            <td className="px-4 py-3">
                              <div className="flex space-x-2">
                                <Button
                                  onClick={() => handleStatusUpdate(lead.id, 'contacted', 'lead')}
                                  size="sm"
                                  variant="outline"
                                >
                                  Mark Contacted
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* Partners Table */}
              {activeTab === 'partners' && (
                <div className="overflow-x-auto">
                  {filteredPartners.length === 0 ? (
                    <div className="text-center py-12">
                      <Handshake className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">No partner applications found</p>
                    </div>
                  ) : (
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {filteredPartners.map((partner) => (
                          <tr key={partner.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div className="font-medium text-gray-900">{partner.first_name} {partner.last_name}</div>
                              <div className="text-sm text-gray-500">{partner.email}</div>
                              <div className="text-sm text-gray-500">{partner.phone}</div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="font-medium text-gray-900">{partner.company_name}</div>
                              <div className="text-sm text-gray-500">{partner.company_website}</div>
                            </td>
                            <td className="px-4 py-3">
                              <span className="px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                {partner.partner_type?.replace('_', ' ').toUpperCase()}
                              </span>
                            </td>
                            <td className="px-4 py-3">{getStatusBadge(partner.status)}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{formatDate(partner.created_at)}</td>
                            <td className="px-4 py-3">
                              <Button
                                onClick={() => window.open(`mailto:${partner.email}`, '_blank')}
                                size="sm"
                                variant="outline"
                              >
                                <Mail className="w-4 h-4 mr-1" />
                                Email
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* Contacts Table */}
              {activeTab === 'contacts' && (
                <div className="overflow-x-auto">
                  {filteredContacts.length === 0 ? (
                    <div className="text-center py-12">
                      <Mail className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">No contact forms found</p>
                    </div>
                  ) : (
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company/City</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Comments</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {filteredContacts.map((contact) => (
                          <tr key={contact.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div className="font-medium text-gray-900">{contact.first_name} {contact.last_name}</div>
                              <div className="text-sm text-gray-500">{contact.email}</div>
                              <div className="text-sm text-gray-500">{contact.phone}</div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm text-gray-900">{contact.company_name || 'N/A'}</div>
                              <div className="text-sm text-gray-500">{contact.city}</div>
                            </td>
                            <td className="px-4 py-3">
                              <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                {contact.reason?.replace('_', ' ')}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                              {contact.comments || 'No comments'}
                            </td>
                            <td className="px-4 py-3">
                              {contact.responded ? (
                                <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                                  <CheckCircle className="w-3 h-3 inline mr-1" />
                                  Responded
                                </span>
                              ) : (
                                <span className="px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                  <Clock className="w-3 h-3 inline mr-1" />
                                  Pending
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">{formatDate(contact.created_at)}</td>
                            <td className="px-4 py-3">
                              <div className="flex space-x-2">
                                {!contact.responded && (
                                  <Button
                                    onClick={() => handleStatusUpdate(contact.id, 'responded', 'contact')}
                                    size="sm"
                                    variant="outline"
                                  >
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                    Mark Responded
                                  </Button>
                                )}
                                <Button
                                  onClick={() => window.open(`mailto:${contact.email}`, '_blank')}
                                  size="sm"
                                  variant="outline"
                                >
                                  <Mail className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminCRM;
